import { useEffect, useState } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { RecommendationTarget } from '../types';
import RecommendeeHeader from '../components/RecommendeeHeader';
import { useLang } from '../LanguageContextProvider';
import RecommendationFootnotes from './RecommendationFootnotes';
// Unit 2.9d (#142) Path B storage write-scoping. Recommenders no longer
// hold direct `s3:PutObject` on the uploads bucket -- the Recommender
// Cognito group is read-only on `public/*` (see
// `amplify/storage/resource.ts`). The ONLY write path is the
// `requestUploadUrl` AppSync mutation, backed by the `presign-upload`
// Lambda (`storage-handler/presign_upload/app.py`): it reads the
// requester's email from the JWT, verifies they are a designated
// recommender for the applicant, builds the canonical S3 key
// SERVER-SIDE, and returns a presigned PUT URL scoped to that exact
// key. The client PUTs the bytes to that URL.
//
// This replaces the #143-era direct `uploadData({ path: "public/" + key,
// ... }).result` write, where the client chose the key -- which is the
// latent write-scoping hole (integration scenario B-3.9: a recommender
// could PUT to any `public/*` prefix). The client now passes only
// `applicantNum` + `fileType` and can never tamper with the prefix.
import { getClient } from '../amplify-client';
import Spinner from './Spinner';
import { getFileKey, validateUploadFile } from '../utils';
import { recommendationFileTypes } from '../recommendationFileTypes';

interface RecommendationUploadFileProps {
    target: RecommendationTarget;
    onClose: () => void;
    setFileUrl: (target: RecommendationTarget, key: string) => Promise<void>;
}

const RecommendationUploadFile: React.FC<RecommendationUploadFileProps> = ({ target, onClose, setFileUrl }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [open, setOpen] = useState(false);
    const { lang } = useLang();

    useEffect(() => {
        setFile(null);
    }, [target]);

    const uploadFile = async () => {
        setOpen(false);
        const fileKey = getFileKey(target);
        console.log(`Uploading ${fileKey}...`);
        try {
            // Step 1: ask the backend for a presigned PUT URL. We pass
            // `applicantNum` + `fileType` (NEVER a raw S3 key) so the
            // Lambda builds the canonical key server-side and can enforce
            // that this requester is a designated recommender for this
            // applicant.
            //
            // `fileType` is the canonical JA document name, which matches
            // the Lambda's VALID_FILE_TYPES allowlist
            // (recommendationFileTypes[...].ja == "調査書" / "推薦状１" / ...).
            // `applicantNum` is coerced to int at this SDK boundary: the
            // schema arg is `a.integer()` while `Recommendee.applicantNum`
            // is a string -- the same boundary-coercion pattern
            // `pages/Applicant.tsx` uses for `parseInt(id, 10)`; a
            // client-wide retype is out of scope here.
            //
            // The data client is built lazily via `getClient()` (never at
            // module scope) for the Amplify-configure-order reason in
            // `amplify-client.ts`. Untyped pending the TS 5 bump (#146),
            // so the mutation result is cast.
            const res = (await getClient().mutations.requestUploadUrl({
                applicantNum: parseInt(target.recommendee.applicantNum, 10),
                fileType: recommendationFileTypes[target.fileType].ja,
            })) as any;
            const presigned = res?.data;
            if (!presigned?.url) {
                // AppSync surfaces the Lambda's UnauthorizedError (stranger,
                // not-a-designated-recommender, or unknown applicant) as a
                // GraphQL error with null data. Carry its message through so
                // the inline error tells the user why the upload was refused.
                throw new Error(
                    res?.errors?.[0]?.message ?? 'Failed to obtain an upload URL',
                );
            }
            // Step 2: PUT the bytes straight to S3 via the scoped URL.
            // Content-Type is set for the stored object's metadata; the
            // Lambda signed only Bucket+Key (SigV4), so S3 accepts this as
            // an unsigned header rather than rejecting the request.
            const putResponse = await fetch(presigned.url, {
                method: 'PUT',
                body: file as File,
                headers: { 'Content-Type': 'application/pdf' },
            });
            if (!putResponse.ok) {
                throw new Error(`Upload failed with status ${putResponse.status}`);
            }
            setFile(null);
            console.log('Complete');
            // `setFileUrl` expects the un-prefixed Gen 1 key shape
            // (`{applicantNum} {name}/{type}.pdf`). Consume the
            // AUTHORITATIVE `key` the Lambda actually signed (the
            // `public/`-prefixed form) and strip the prefix, rather than
            // re-using the locally computed `fileKey`. This removes the
            // cross-language duplicated-key invariant: if the client's
            // `getFileKey()` and the Lambda's `build_object_key()` ever
            // drift (e.g. a name-normalization change on one side), the
            // refresh callback still points at the real uploaded object.
            setFileUrl(target, (presigned.key as string).replace(/^public\//, ''));
        } catch (err) {
            setError(err as Error);
            console.error('Error uploading file: ', err);
            setIsLoading(false);
        }

    };

    const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles, fileRejections, event) => {
        setError(null);

        // react-dropzone shunts files whose sniffed MIME doesn't match
        // `accept` into `fileRejections`. The most common cause in real
        // cycles is teachers saving the file without a `.pdf` extension
        // (issue #123) -- the file is a PDF byte-for-byte but the OS
        // labels it `application/octet-stream`, so the dropzone rejects
        // it before it ever reaches `acceptedFiles`. Surface a message
        // that names the actual cause instead of "no file selected".
        if (fileRejections.length > 0 && acceptedFiles.length === 0) {
            const rejectionMessage = lang === 'en'
                ? 'This file isn\'t recognized as a PDF. If the file name doesn\'t end in ".pdf", please rename it (or re-export from your PDF software) and try again.'
                : 'このファイルはPDFとして認識されませんでした。ファイル名が「.pdf」で終わっていない場合は、リネーム（またはPDFソフトから再書き出し）して再度お試しください。';
            setError(new Error(rejectionMessage));
            return;
        }

        // Cancel-the-picker / no-file edge case. The dropzone shouldn't
        // normally fire `onDrop` with both arrays empty, but guard so the
        // validator doesn't see an undefined File.
        const candidate = acceptedFiles[0];
        if (!candidate) {
            return;
        }

        const validationError = validateUploadFile(candidate, lang);
        if (validationError) {
            // Surface inline above a live dropzone (see renderContent).
            // Don't enter the loading state and don't open the confirm
            // dialog -- the user picks a different file from the same
            // dropzone, and re-dropping clears the error.
            setError(new Error(validationError));
            setIsLoading(false);
            setFile(null);
            setOpen(false);
            return;
        }
        setIsLoading(true);
        setFile(candidate);
        setOpen(true);
    };

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject
    } = useDropzone({
        onDrop,
        accept: { 'application/pdf': ['.pdf'] },
        maxFiles: 1
    });

    const handleClose = () => {
        setOpen(false);
        setFile(null);
        setIsLoading(false);
    };

    const renderContent = () => {
        if (isLoading) return <Spinner size={48} />;

        // Render the error inline ABOVE the dropzone so the user can
        // re-drop without a remount. Previously the dropzone was
        // swapped out entirely when `error` was truthy, which left a
        // dead-end for the most common operator mistake (selecting an
        // empty PDF). Re-firing onDrop clears the error via setError(null).
        return (
            <Stack spacing={1} height="100%">
                {error && (
                    <Typography sx={{ color: 'error.main' }}>{error.message}</Typography>
                )}
                <Box
                    sx={{
                        border: theme => {
                            let color: string;
                            if (isDragAccept) color = theme.palette.primary.main;
                            else if (isDragReject) color = theme.palette.error.main;
                            else if (isFocused) color = theme.palette.text.primary;
                            else color = theme.palette.grey[500];
                            return `1px dashed ${color}`
                        },
                        bgcolor: theme => theme.palette.grey[100],
                        color: theme => theme.palette.grey[500],
                        cursor: 'pointer',
                        flexGrow: 1
                    }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    {...getRootProps()}
                >
                    <input {...getInputProps()} />
                    <Typography>{lang === 'en' ? 'Click here or drop your file here to upload it' : 'クリックもしくはドラッグ&ドロップでファイルをアップロード'}</Typography>
                </Box>
            </Stack>
        );
    };


    return (
        <Stack padding="32px" spacing={3}>
            <RecommendeeHeader target={target} onClose={onClose} />
            <Box height={error ? "144px" : "96px"}>
                {renderContent()}
            </Box>
            <RecommendationFootnotes fileType={target.fileType} />
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>{lang === 'en' ? 'You are about to upload a file' : 'アップロード確認画面'}</DialogTitle>
                <DialogContent>
                    <Typography variant="h6">{file?.name}</Typography>
                    <Typography>{lang === 'en' ? 'Click "Send" to upload this file' : 'こちらのファイルをアップロードするには「送信」ボタンを押してください。'}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleClose}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
                    <Button onClick={uploadFile}>{lang === 'en' ? 'Send' : '送信'}</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default RecommendationUploadFile;